import React from 'react';
import {
    Card,
    Space,
    Tag,
    Typography,
    Descriptions
} from 'antd';
import {
  BuildOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { HK_REGIONS_AND_DISTRICTS } from '../../utils/hk-regions';
import DeliveryProviderCard from './DeliveryProviderCard';
import { SfExpressOrder } from '../../types/sf-express.types';
import { JdExpressOrder } from '../../types/jd-express.types';

const { Text } = Typography;

// Helper function to get localized region name
const getLocalizedRegionName = (regionCode: string, locale: string = 'zh-HK'): string => {
    const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
    if (!region) return regionCode;

    switch (locale) {
        case 'en':
            return region.name_en;
        case 'zh-CN':
            return region.name_zh_cn;
        case 'zh-HK':
        default:
            return region.name_zh_hk;
    }
};

// Helper function to get localized district name
const getLocalizedDistrictName = (regionCode: string, districtCode: string, locale: string = 'zh-HK'): string => {
    const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
    if (!region) return districtCode;

    const district = region.districts.find(d => d.code === districtCode);
    if (!district) return districtCode;

    switch (locale) {
        case 'en':
            return district.name_en;
        case 'zh-CN':
            return district.name_zh_cn;
        case 'zh-HK':
        default:
            return district.name_zh_hk;
    }
};

// Helper function to format address with locale-specific formatting
const formatAddress = (address: any, t: (k: string, options?: { value: string }) => string, locale: string = 'zh-HK'): string => {
    if (!address) return t('common.na');

    const localizedRegion = address.region ? getLocalizedRegionName(address.region, locale) : '';
    const localizedDistrict = address.district && address.region ?
        getLocalizedDistrictName(address.region, address.district, locale) :
        address.district;

    // Locale-specific address formatting
    const isChinese = locale.startsWith('zh');

    let parts: string[] = [];

    if (isChinese) {
        // Chinese address format: Country, Region, District, Building, Floor, Unit, Street
        parts = [
            address.country,
            localizedRegion,
            localizedDistrict,
            address.building_name,
            address.block && `${t('addresses.value.block', { value: address.block })}`,
            address.floor && `${t('addresses.value.floor', { value: address.floor })}`,
            address.unit && `${t('addresses.value.unit', { value: address.unit })}`,
            address.street_address,
            address.postal_code
        ];
    } else {
        // English address format: Unit, Floor, Building, Street, District, Region, Postal Code, Country
        parts = [
            address.unit && `${t('addresses.value.unit', { value: address.unit })}`,
            address.floor && `${t('addresses.value.floor', { value: address.floor })}`,
            address.block && `${t('addresses.value.block', { value: address.block })}`,
            address.building_name,
            address.street_address,
            localizedDistrict,
            localizedRegion,
            address.postal_code,
            address.country
        ];
    }

    const filteredParts = parts.filter(Boolean);
    return filteredParts.length > 0 ? filteredParts.join(', ') : t('common.na');
};

// Helper function to get localized address type
const getLocalizedAddressType = (type: string, t: (k: string) => string): string => {
    switch (type) {
        case 'home':
            return t('addresses.homeAddress');
        case 'work':
            return t('addresses.workAddress');
        case 'other':
            return t('addresses.otherAddress');
        default:
            return t('addresses.address');
    }
};


interface DeliveryAddress {
    id: string;
    type: string;
    is_default: boolean;
    street_address: string;
    unit?: string | null;
    floor?: string | null;
    building_name?: string | null;
    district?: string | null;
    region?: string | null;
    postal_code?: string | null;
    country?: string | null;
    contact_name?: string | null;
    phone_number?: string | null;
    override_address?: string | null;
}


interface DeliveryAddressCardProps {
    deliveryAddress: DeliveryAddress;
    deliveryNote?: string | null;
    appointmentId?: string;
    onCreateSfExpressOrder: () => Promise<void>;
    creatingSfOrder: boolean;
    onCreateJdExpressOrder?: () => Promise<void>;
    creatingJdOrder?: boolean;
    onCloudPrintWaybill?: (waybillNo: string) => Promise<void>;
    onDeleteSfExpressOrder?: () => void;
    onDeleteJdExpressOrder?: () => void;
    showSfExpressButton?: boolean;
    showJdExpressButton?: boolean;
    showDeliveryNote?: boolean;
    showRouteLookup?: boolean;
    style?: React.CSSProperties;
    locale?: string;
    sfExpressOrders?: SfExpressOrder[];
    jdExpressOrders?: JdExpressOrder[];
    onViewSfExpressPdf?: (waybillNo: string) => void;
    onViewJdExpressPdf?: (waybillNo: string) => void;
}

const DeliveryAddressCard: React.FC<DeliveryAddressCardProps> = ({
    deliveryAddress,
    deliveryNote,
    appointmentId,
    onCreateSfExpressOrder,
    creatingSfOrder,
    onCreateJdExpressOrder,
    creatingJdOrder = false,
    onCloudPrintWaybill,
    onDeleteSfExpressOrder,
    onDeleteJdExpressOrder,
    showSfExpressButton = true,
    showJdExpressButton = true,
    showDeliveryNote = true,
    showRouteLookup = true,
    style,
    locale,
    sfExpressOrders = [],
    jdExpressOrders = [],
    onViewSfExpressPdf,
    onViewJdExpressPdf
}) => {
    const { t, currentLanguage } = useTranslation();

    // Use provided locale or fall back to current language
    const activeLocale = locale || currentLanguage;
    
    // Enhanced locale detection and validation
    const isValidLocale = (loc: string): boolean => {
        return ['en', 'zh-CN', 'zh-HK'].includes(loc);
    };
    
    const validatedLocale = isValidLocale(activeLocale) ? activeLocale : 'en';
    
    // Get locale-specific styling
    const textAlign = 'left'; // Currently all supported locales are LTR
    const direction = 'ltr';

    return (
        <>
            <Card
                title={t('appointments.deliveryAddress')}
                style={{ 
                    marginBottom: 24, 
                    textAlign,
                    direction,
                    ...style 
                }}
                extra={
                    <Tag color="blue">
                        {deliveryAddress.is_default ? t('addresses.default') : t('addresses.selected')}
                    </Tag>
                }
            >
                <Descriptions column={1} bordered>
                    <Descriptions.Item
                        label={
                            <Space>
                                <BuildOutlined />
                                {getLocalizedAddressType(deliveryAddress.type, t)}
                                {deliveryAddress.is_default && <Tag color="green">{t('addresses.default')}</Tag>}
                            </Space>
                        }
                    >
                        <div>
                            <Text strong>
                                {deliveryAddress.override_address || formatAddress(deliveryAddress, t, activeLocale)}
                            </Text>
                            {!deliveryAddress.override_address && deliveryAddress.type && (
                                <div style={{ marginTop: 4 }}>
                                    <Tag color="blue">{getLocalizedAddressType(deliveryAddress.type, t)}</Tag>
                                </div>
                            )}
                        </div>
                    </Descriptions.Item>
                    {deliveryAddress.contact_name && (
                        <Descriptions.Item label={t('addresses.contactName')}>
                            <Text strong>{deliveryAddress.contact_name}</Text>
                        </Descriptions.Item>
                    )}
                    {deliveryAddress.phone_number && (
                        <Descriptions.Item label={t('common.phoneNumber')}>
                            <Text strong>{deliveryAddress.phone_number}</Text>
                        </Descriptions.Item>
                    )}
                </Descriptions>

                {showDeliveryNote && deliveryNote && (
                    <div style={{ marginTop: 16 }}>
                        <Text strong>{t('addresses.deliveryNote')}:</Text>
                        <div style={{ marginTop: 4 }}>
                            <Text>{deliveryNote}</Text>
                        </div>
                    </div>
                )}

            </Card>

            {/* Delivery Provider Card Component (master card with separate SF and JD components) */}
            {(showSfExpressButton || showJdExpressButton) && (
                <DeliveryProviderCard
                    appointmentId={appointmentId}
                    onCreateSfExpressOrder={onCreateSfExpressOrder}
                    creatingSfOrder={creatingSfOrder}
                    onCreateJdExpressOrder={onCreateJdExpressOrder}
                    creatingJdOrder={creatingJdOrder}
                    jdExpressOrders={jdExpressOrders}
                    onCloudPrintWaybill={onCloudPrintWaybill}
                    onViewSfExpressPdf={onViewSfExpressPdf}
                    onViewJdExpressPdf={onViewJdExpressPdf}
                    onDeleteSfExpressOrder={onDeleteSfExpressOrder}
                    onDeleteJdExpressOrder={onDeleteJdExpressOrder}
                    showSfExpressButton={showSfExpressButton}
                    showJdExpressButton={showJdExpressButton}
                    showRouteLookup={showRouteLookup}
                    locale={locale}
                    sfExpressOrders={sfExpressOrders}
                    showTitle={false}
                />
            )}
        </>
    );
};

export default DeliveryAddressCard;
